import { getLiberiaApiBaseUrl } from '@/utils/liberiaApi';

export type StoreInterestButton = 'app_store' | 'play_store';

type StoreInterestEvent = 'page_view' | 'store_click';

function currentPageAndReferrer(): { page: string | null; referrer: string | null } {
  if (typeof window === 'undefined') {
    return { page: null, referrer: null };
  }
  const path = window.location?.pathname || '/';
  const search = window.location?.search || '';
  const page = `${path}${search}`.slice(0, 300);
  const referrer = typeof document !== 'undefined' && document.referrer ? document.referrer : null;
  return { page, referrer };
}

/**
 * Tell the API about a get-app open or a store-link tap.
 * Failure must not change where the store link goes.
 */
function recordStoreInterest(event: StoreInterestEvent, store?: StoreInterestButton): void {
  const base = getLiberiaApiBaseUrl();
  if (!base || typeof fetch !== 'function') {
    return;
  }

  const origin = currentPageAndReferrer();
  const body: {
    event: StoreInterestEvent;
    store?: StoreInterestButton;
    page: string | null;
    referrer: string | null;
  } = {
    event,
    page: origin.page,
    referrer: origin.referrer,
  };
  if (store) {
    body.store = store;
  }

  try {
    void fetch(`${base}/api/v1/public/store-interest`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => undefined);
  } catch {
    // The store link still opens if the counter is unreachable.
  }
}

/** One get-app page open. This is not a store-link tap. */
export function recordGetAppPageView(): void {
  recordStoreInterest('page_view');
}

/** One tap on an App Store or Play Store link, including a repeat tap. */
export function recordStoreLinkClick(store: StoreInterestButton): void {
  recordStoreInterest('store_click', store);
}

export interface StoreInterestClick {
  id: string;
  store: StoreInterestButton | null;
  storeLabel: string | null;
  page: string | null;
  referrer: string | null;
  country: string | null;
  city: string | null;
  createdAt: string | null;
}

export interface StoreInterestSummary {
  pageOpens: number;
  pageOpensSinceDeploy: number;
  appStoreLinkTaps: number;
  playStoreLinkTaps: number;
  backfill: {
    pageOpens: number;
    windowStart: string | null;
    windowEnd: string | null;
    note: string | null;
  } | null;
  recentClicks: StoreInterestClick[];
}

/** QLS Admin totals and recent taps. The public site does not call this. */
export async function fetchStoreInterestSummary(): Promise<StoreInterestSummary> {
  const base = getLiberiaApiBaseUrl();
  if (!base) {
    throw new Error('QLS API base URL is not configured');
  }

  const response = await fetch(`${base}/api/v1/admin/store-interest`, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Store interest could not be loaded.');
  }
  return payload?.data as StoreInterestSummary;
}
